import type {
  CameraState,
  ProjectedPoint,
  TrackballCamera,
  ViewCamera,
} from '../types';
import { quaternionFromAxes, rotateVector } from './quaternion';

/**
 * The camera's orthonormal basis, derived from yaw and pitch.
 *
 * Built explicitly rather than as a matrix product so each axis is readable on its
 * own and the basis can be asserted orthonormal in a test - which is the property that
 * makes depth sorting trustworthy. `forward` points from the plot toward the eye, so a
 * larger dot product with it means nearer the viewer.
 */
export type CameraBasis = {
  right: readonly [number, number, number];
  up: readonly [number, number, number];
  forward: readonly [number, number, number];
};

/** Keeps pitch off the poles, where yaw stops having a defined effect on screen. */
const PITCH_LIMIT = Math.PI / 2 - 0.01;

export const clampPitch = (pitch: number): number =>
  Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));

export const getCameraBasis = (yaw: number, pitch: number): CameraBasis => {
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);
  const cosPitch = Math.cos(pitch);
  const sinPitch = Math.sin(pitch);

  return {
    right: [-sinYaw, cosYaw, 0],
    up: [-cosYaw * sinPitch, -sinYaw * sinPitch, cosPitch],
    forward: [cosYaw * cosPitch, sinYaw * cosPitch, sinPitch],
  };
};

/**
 * Which corner of the base plane is furthest from the eye, as `[x, y]` in the unit square.
 *
 * A fact about the camera and nothing else - a yaw picks a quadrant, and the quadrant picks a
 * corner - so it lives with the rest of the camera maths rather than in a renderer. Two things read
 * it and neither owns it: the axes put the vertical axis and the tick labels at that corner so they
 * never cross the surface, and the box stands its walls on the two faces meeting it. Homing it in
 * either would make the other depend on a module it has no business knowing about.
 */
/** The axes of either orbit camera. A turntable's pitch is clamped here, as the projector always has. */
export const toCameraBasis = (camera: ViewCamera): CameraBasis => {
  if ('orientation' in camera) {
    const { orientation } = camera;
    return {
      right: rotateVector(orientation, [1, 0, 0]),
      up: rotateVector(orientation, [0, 1, 0]),
      forward: rotateVector(orientation, [0, 0, 1]),
    };
  }
  return getCameraBasis(camera.yaw, clampPitch(camera.pitch));
};

/** A trackball camera opening on exactly the view `camera` shows. */
export const toTrackballCamera = ({
  yaw,
  pitch,
  ...lens
}: CameraState): TrackballCamera => {
  const { right, up, forward } = getCameraBasis(yaw, clampPitch(pitch));
  return { ...lens, orientation: quaternionFromAxes(right, up, forward) };
};

export type BackCorner = [x: number, y: number];

const CORNERS: BackCorner[] = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
];

export const getBackCorner = (yaw: number): BackCorner => {
  const normalised = ((yaw % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const quadrant = Math.floor(normalised / (Math.PI / 2)) % 4;

  return CORNERS[quadrant];
};

const dot = (
  [ax, ay, az]: readonly [number, number, number],
  x: number,
  y: number,
  z: number
): number => ax * x + ay * y + az * z;

/** Floor on the homogeneous divisor, so a point at extreme depth cannot invert or blow up. */
const MINIMUM_DIVISOR = 0.1;

/**
 * Weak perspective, as a homogeneous divide.
 *
 * True perspective would let a peak at the front of the plane dwarf an equal peak at the back,
 * which breaks the one thing a surface chart is for - comparing heights across the plane. A small
 * `perspective` keeps the depth cue without that cost, and collapses to exactly 1 at 0.
 *
 * Written as `1 / (1 - k·depth)` rather than `1 + k·depth` specifically so it is a projective
 * transform: that form is exactly what a 4x4 matrix and a `w` divide produce, which is how a GPU
 * pipeline has to express it. The two differ by about a tenth at the corners of the plot, so the
 * near-equivalent would leave a hardware renderer quietly disagreeing with this one.
 */
const getForeshortening = (depth: number, perspective: number): number =>
  1 / Math.max(MINIMUM_DIVISOR, 1 - perspective * depth);

export type Projector = (x: number, y: number, z: number) => ProjectedPoint;

/**
 * Builds a projector for one camera and viewport.
 *
 * Input coordinates are expected pre-normalised into roughly [-1, 1] on x and y and
 * [0, 1] on z, so `zoom` and the viewport do the rest of the work and the camera stays
 * independent of the data's units.
 */
export const createProjector = (
  camera: ViewCamera,
  viewport: { width: number; height: number }
): Projector => {
  const { right, up, forward } = toCameraBasis(camera);
  const centreX = viewport.width / 2;
  const centreY = viewport.height / 2;
  /* One scale for both axes: a surface has to stay unsheared, so the smaller viewport
     dimension governs and the plot letterboxes in the larger one. */
  const pixelsPerUnit =
    (Math.min(viewport.width, viewport.height) / 2) * camera.zoom;

  /* The pivot, in the same z-scaled space the points are dotted in - which is what keeps this
     projector agreeing with `toViewProjectionMatrix`, where the translation rides in column 3
     against the z-scaled basis vectors. Two projections of one camera that disagreed would put a
     label somewhere the mark it names is not. */
  const [targetX, targetY, targetZ] = camera.target;
  const scaledTargetZ = targetZ * camera.heightScale;

  return (x, y, z) => {
    const scaledZ = z * camera.heightScale - scaledTargetZ;
    const localX = x - targetX;
    const localY = y - targetY;
    const depth = dot(forward, localX, localY, scaledZ);
    const scale = getForeshortening(depth, camera.perspective);
    const horizontal = dot(right, localX, localY, scaledZ) * scale;
    const vertical = dot(up, localX, localY, scaledZ) * scale;

    return {
      screenX: centreX + horizontal * pixelsPerUnit,
      /* Negated: screen y grows downward while the camera's up axis grows upward. */
      screenY: centreY - vertical * pixelsPerUnit,
      depth,
      scale,
    };
  };
};

export const DEFAULT_CAMERA: CameraState = {
  /* Three-quarter view. Straight-on hides the depth axis entirely, and 45 degrees
     makes the two horizontal axes symmetric and so harder to tell apart. */
  yaw: Math.PI / 5,
  pitch: 0.62,
  zoom: 1,
  perspective: 0.22,
  heightScale: 0.55,
  /* The origin, which is where every camera here has always pivoted. */
  target: [0, 0, 0],
};
